import { defineChain } from 'viem';
import {
  type Chain,
  mainnet,
  polygon,
  arbitrum,
  avalanche,
  base,
  bsc,
  optimism,
  scroll,
  linea,
  mantle,
  unichain,
  plasma,
  abstract,
  monad,
  ink,
  hyperEvm,
  robinhood as robinhoodViem,
  arc as arcViem,
} from 'viem/chains';

import { ClientConfigurationError } from '@/lib/errors/client';

const CHAIN_ID_SOLANA = 1399811149;

export const solana = defineChain({
  name: 'Solana',
  id: CHAIN_ID_SOLANA,
  sourceId: CHAIN_ID_SOLANA,
  nativeCurrency: { name: 'Solana', symbol: 'SOL', decimals: 9 },
  rpcUrls: { default: { http: ['https://api.mainnet-beta.solana.com'] } },
  blockExplorers: {
    default: {
      name: 'Solscan',
      url: 'https://solscan.io/',
      apiUrl: 'https://pro-api.solscan.io/pro-api-docs/v2.0',
    },
  },
});

// Override Robinhood chain definitions to use etherscan of blockscout as block explorer
export const robinhoodChain = defineChain({
  ...robinhoodViem,
  blockExplorers: { default: { name: 'Etherscan', url: 'https://robin.etherscan.io' } },
});

// Add rpc urls, block explorer and multicall attributes to Arc chain definitions
export const arc = defineChain({
  ...arcViem,
  rpcUrls: { default: { http: ['https://rpc.arc-scan.org'] } },
  blockExplorers: { default: { name: 'Etherscan', url: 'https://arc.etherscan.io' } },
  contracts: { multicall3: { address: '0xca11bde05977b3631167028862be2a173976ca11' } },
});

export const CHAIN_IDS = {
  SOLANA: solana.id,
  MAINNET: mainnet.id,
  BASE: base.id,
  UNICHAIN: unichain.id,
  BSC: bsc.id,
  MATIC: polygon.id,
  AVALANCHE: avalanche.id,
  ARBITRUM: arbitrum.id,
  OPTIMISM: optimism.id,
  SCROLL: scroll.id,
  LINEA: linea.id,
  MANTLE: mantle.id,
  PLASMA: plasma.id,
  MONAD: monad.id,
  ABSTRACT: abstract.id,
  HYPEREVM: hyperEvm.id,
  ROBINHOOD: robinhoodChain.id,
  INK: ink.id,
  ARC: arc.id,
} as const;

// source: https://dune.com/queries/3285438/5499741
export const BLOCK_CONFIRMATION_TIME_IN_SECONDS: {
  [key: keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID]: number;
} = {
  [CHAIN_IDS.MAINNET]: 12,
  [CHAIN_IDS.BSC]: 0.75,
  [CHAIN_IDS.MATIC]: 2.3,
  [CHAIN_IDS.AVALANCHE]: 2,
  [CHAIN_IDS.ARBITRUM]: 0.26,
  [CHAIN_IDS.OPTIMISM]: 2,
  [CHAIN_IDS.BASE]: 0.2,
  [CHAIN_IDS.SCROLL]: 2.9,
  [CHAIN_IDS.LINEA]: 2.02,
  [CHAIN_IDS.MANTLE]: 2,
  [CHAIN_IDS.MONAD]: 0.4,
  [CHAIN_IDS.SOLANA]: 0.4,
  [CHAIN_IDS.UNICHAIN]: 1,
  [CHAIN_IDS.PLASMA]: 1,
  [CHAIN_IDS.ABSTRACT]: 0.6,
  [CHAIN_IDS.HYPEREVM]: 1,
  [CHAIN_IDS.ROBINHOOD]: 1,
  [CHAIN_IDS.INK]: 1,
  [CHAIN_IDS.ARC]: 0.5,
};
// Quote refresh cadence (ms): faster-settling chains re-fetch quotes more
// aggressively, since on-chain prices track block production.
const SLOW_QUOTE_REFRESH_INTERVAL_MS = 12_000;
const FAST_QUOTE_REFRESH_INTERVAL_MS = 6_000;
const FAST_CHAIN_BLOCK_TIME_THRESHOLD_SECONDS = 6;

/**
 * Quote refresh interval (ms) for a chain. Chains absent from the block-time
 * map fall back to mainnet's 12s, i.e. the slower cadence.
 */
export const getQuoteRefreshInterval = (chainId: number): number =>
  (BLOCK_CONFIRMATION_TIME_IN_SECONDS[chainId] ?? 12) <= FAST_CHAIN_BLOCK_TIME_THRESHOLD_SECONDS
    ? FAST_QUOTE_REFRESH_INTERVAL_MS
    : SLOW_QUOTE_REFRESH_INTERVAL_MS;

// The intents API rejects a submit when less than this remains until the deadline.
const INTENTS_SUBMIT_MIN_REMAINING_SECONDS = 30;
// Budget for the user to review the quote and sign up to twice (permit, trade).
const INTENTS_USER_ACTION_WINDOW_SECONDS = 60;

/**
 * Lifetime (seconds) for an intents trade deadline: it must outlast the current
 * poll, the user's signing, and the upstream submit threshold.
 */
export const getIntentsDeadlineSeconds = (chainId: number): number =>
  getQuoteRefreshInterval(chainId) / 1000 +
  INTENTS_USER_ACTION_WINDOW_SECONDS +
  INTENTS_SUBMIT_MIN_REMAINING_SECONDS;

// Balance refresh cadence (ms): mirrors the legacy "every 5 blocks" behavior by
// scaling with block time, clamped so fast chains don't hammer the RPC and slow
// chains stay reasonably fresh.
const BALANCE_REFRESH_BLOCKS = 5;
const MIN_BALANCE_REFRESH_INTERVAL_MS = 6_000;
const MAX_BALANCE_REFRESH_INTERVAL_MS = 30_000;

/**
 * Balance refresh interval (ms) for a chain, derived from block time (~5 blocks)
 * and clamped. Chains absent from the block-time map fall back to mainnet's 12s.
 */
export const getBalanceRefreshInterval = (chainId: number | undefined): number => {
  const blockTimeSeconds =
    (chainId != null ? BLOCK_CONFIRMATION_TIME_IN_SECONDS[chainId] : undefined) ?? 12;
  const intervalMs = blockTimeSeconds * BALANCE_REFRESH_BLOCKS * 1000;
  return Math.min(
    Math.max(intervalMs, MIN_BALANCE_REFRESH_INTERVAL_MS),
    MAX_BALANCE_REFRESH_INTERVAL_MS,
  );
};

export const SUPPORTED_CHAINS: Chain[] = [
  mainnet,
  base,
  robinhoodChain,
  arc,
  bsc,
  solana,
  polygon,
  hyperEvm,
  monad,
  arbitrum,
  ink,
  optimism,
  abstract,
  plasma,
  avalanche,
  linea,
  mantle,
  scroll,
  unichain,
];

export const PAIRS_SUPPORTED_CHAIN_IDS: number[] = [
  CHAIN_IDS.MAINNET,
  CHAIN_IDS.OPTIMISM,
  CHAIN_IDS.ARBITRUM,
  CHAIN_IDS.BASE,
  CHAIN_IDS.AVALANCHE,
  CHAIN_IDS.MATIC,
  CHAIN_IDS.BSC,
];

export const LIMIT_ORDER_SUPPORTED_CHAINS = [
  mainnet,
  base,
  robinhoodChain,
  arc,
  bsc,
  solana,
  hyperEvm,
  monad,
  arbitrum,
  ink,
  avalanche,
  optimism,
  polygon,
  plasma,
] satisfies Chain[];

export type LimitOrderSupportedChainId = (typeof LIMIT_ORDER_SUPPORTED_CHAINS)[number]['id'];

const CROSS_CHAIN_CHAINS: Chain[] = [
  solana,
  robinhoodChain,
  ink,
  arc,
  mainnet,
  polygon,
  arbitrum,
  base,
  unichain,
  mantle,
  scroll,
  linea,
  optimism,
  bsc,
  avalanche,
  abstract,
  monad,
  plasma,
  hyperEvm,
];

// Using SUPPORTED_CHAINS order to make both chain lists consistent
export const CROSS_CHAIN_SUPPORTED_CHAINS: Chain[] = SUPPORTED_CHAINS.filter((chain) =>
  CROSS_CHAIN_CHAINS.includes(chain),
);

export const SUPPORTED_CHAINS_BY_CHAIN_ID = SUPPORTED_CHAINS.reduce<{ [key: number]: Chain }>(
  (supportedChainsById, chain: Chain) => {
    supportedChainsById[chain.id] = chain;
    return supportedChainsById;
  },
  {},
);

export const NATIVE_TOKEN_SYMBOL_PER_CHAIN: { [key: number]: string } = SUPPORTED_CHAINS.reduce(
  (memo, chain: Chain) => {
    memo[chain.id] = chain.nativeCurrency.symbol;
    return memo;
  },
  {} as { [key: number]: string },
);

export const WRAPPED_NATIVE_TOKEN_SYMBOL_PER_CHAIN: Partial<Record<SupportedChainIDs, string>> = {
  [CHAIN_IDS.MAINNET]: 'WETH',
  [CHAIN_IDS.BSC]: 'WBNB',
  [CHAIN_IDS.MATIC]: 'WMATIC',
  [CHAIN_IDS.AVALANCHE]: 'WAVAX',
  [CHAIN_IDS.OPTIMISM]: 'WETH',
  [CHAIN_IDS.ARBITRUM]: 'WETH',
  [CHAIN_IDS.BASE]: 'WETH',
  [CHAIN_IDS.SCROLL]: 'WETH',
  [CHAIN_IDS.SOLANA]: 'WSOL',
  [CHAIN_IDS.LINEA]: 'WETH',
  [CHAIN_IDS.MANTLE]: 'WMNT',
  [CHAIN_IDS.UNICHAIN]: 'WETH',
  [CHAIN_IDS.PLASMA]: 'WXPL',
  [CHAIN_IDS.MONAD]: 'WMON',
  [CHAIN_IDS.ABSTRACT]: 'WETH',
  [CHAIN_IDS.HYPEREVM]: 'WHYPE',
  [CHAIN_IDS.ROBINHOOD]: 'WETH',
  [CHAIN_IDS.INK]: 'WETH',
};

export type SupportedChainIDs = keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID;
export type SupportedChainId = (typeof CHAIN_IDS)[keyof typeof CHAIN_IDS];

// Chains where the Intents API (0x `/intents`) is available. Matcha Auto routes
// trades through this lane, so a chain must be in the entitled `/intents/chains`
// set to appear here.
export const CHAIN_IDS_SUPPORT_INTENTS: number[] = [
  CHAIN_IDS.MAINNET,
  CHAIN_IDS.MATIC,
  CHAIN_IDS.ARBITRUM,
  CHAIN_IDS.BASE,
  CHAIN_IDS.MONAD,
  CHAIN_IDS.SCROLL,
  CHAIN_IDS.OPTIMISM,
  CHAIN_IDS.BSC,
  CHAIN_IDS.AVALANCHE,
  CHAIN_IDS.MANTLE,
  CHAIN_IDS.PLASMA,
  CHAIN_IDS.UNICHAIN,
  CHAIN_IDS.HYPEREVM,
  CHAIN_IDS.ROBINHOOD,
  CHAIN_IDS.INK,
  CHAIN_IDS.ARC,
];

export const EIP_7702_SUPPORTED_CHAINS: number[] = [
  CHAIN_IDS.MAINNET,
  CHAIN_IDS.BASE,
  CHAIN_IDS.ARBITRUM,
  CHAIN_IDS.OPTIMISM,
  CHAIN_IDS.MATIC,
  CHAIN_IDS.BSC,
  CHAIN_IDS.UNICHAIN,
  CHAIN_IDS.MONAD,
  CHAIN_IDS.ROBINHOOD,
  CHAIN_IDS.INK,
  CHAIN_IDS.ARC,
];

export const SUPPORTED_CHAIN_IDS = SUPPORTED_CHAINS.map((chain) => chain.id);
export const SUPPORTED_CHAIN_IDS_LIMIT_ORDERS: number[] = LIMIT_ORDER_SUPPORTED_CHAINS.map(
  (c) => c.id,
);
export const SUPPORTED_CHAIN_IDS_CROSS_CHAIN = CROSS_CHAIN_SUPPORTED_CHAINS.map((c) => c.id);
export const NETWORK_NAME_PER_CHAIN_ID = SUPPORTED_CHAINS.reduce<{
  [key: number]: string;
}>(
  (chainNameByChainId, chain: Chain) => {
    chainNameByChainId[chain.id] =
      chain.id === CHAIN_IDS.BSC
        ? 'BNB Chain' // override BSC name to be "BNB Chain"
        : chain.name;

    return chainNameByChainId;
  },
  { [solana.id]: solana.name },
);

/*
The three reducers defined below are used to power the URL structure.
Due to Wagmi's config, we need to define the network name for Mainnet to 'ethereum'
since Wagmi has it defined as 'homestead'. We also are doing a custom mapping for
polygon, since Wagmi's config names the network 'matic'.
*/

/**
 *
 * Mapping from network name to chain id.
 *
 */
export const CHAIN_ID_PER_NETWORK_URL_PATH = SUPPORTED_CHAINS.reduce<{ [key: string]: number }>(
  (chainIdByChainName, chain: Chain) => {
    switch (chain.id) {
      case CHAIN_IDS.MAINNET:
        chainIdByChainName['ethereum'] = CHAIN_IDS.MAINNET;
        break;
      case CHAIN_IDS.MATIC:
        chainIdByChainName['polygon'] = CHAIN_IDS.MATIC;
        break;
      case CHAIN_IDS.BSC:
        chainIdByChainName['bnb'] = CHAIN_IDS.BSC;
        break;
      case CHAIN_IDS.ARBITRUM:
        chainIdByChainName['arbitrum'] = CHAIN_IDS.ARBITRUM;
        break;
      case CHAIN_IDS.OPTIMISM:
        chainIdByChainName['optimism'] = CHAIN_IDS.OPTIMISM;
        break;
      case CHAIN_IDS.AVALANCHE:
        chainIdByChainName['avalanche'] = CHAIN_IDS.AVALANCHE;
        break;
      case CHAIN_IDS.BASE:
        chainIdByChainName['base'] = CHAIN_IDS.BASE;
        break;
      case CHAIN_IDS.SCROLL:
        chainIdByChainName['scroll'] = CHAIN_IDS.SCROLL;
        break;
      case CHAIN_IDS.LINEA:
        chainIdByChainName['linea'] = CHAIN_IDS.LINEA;
        break;
      case CHAIN_IDS.MANTLE:
        chainIdByChainName['mantle'] = CHAIN_IDS.MANTLE;
        break;
      case CHAIN_IDS.MONAD:
        chainIdByChainName['monad'] = CHAIN_IDS.MONAD;
        break;
      case CHAIN_IDS.SOLANA:
        chainIdByChainName['solana'] = CHAIN_IDS.SOLANA;
        break;
      case CHAIN_IDS.UNICHAIN:
        chainIdByChainName['unichain'] = CHAIN_IDS.UNICHAIN;
        break;
      case CHAIN_IDS.PLASMA:
        chainIdByChainName['plasma'] = CHAIN_IDS.PLASMA;
        break;
      case CHAIN_IDS.ABSTRACT:
        chainIdByChainName['abstract'] = CHAIN_IDS.ABSTRACT;
        break;
      case CHAIN_IDS.HYPEREVM:
        chainIdByChainName['hyperevm'] = CHAIN_IDS.HYPEREVM;
        break;
      case CHAIN_IDS.ROBINHOOD:
        chainIdByChainName['robinhood'] = CHAIN_IDS.ROBINHOOD;
        break;
      case CHAIN_IDS.INK:
        chainIdByChainName['ink'] = CHAIN_IDS.INK;
        break;
      case CHAIN_IDS.ARC:
        chainIdByChainName['arc'] = CHAIN_IDS.ARC;
        break;
      default:
        throw new ClientConfigurationError('Supported chain missing slug');
    }

    return chainIdByChainName;
  },
  {},
);

export const NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID = Object.entries(
  CHAIN_ID_PER_NETWORK_URL_PATH,
).reduce<{
  [key: number]: string;
}>((chainNameByChainId, [slug, id]) => {
  chainNameByChainId[id] = slug;
  return chainNameByChainId;
}, {});

/**
 * The names of block explorers by chain.
 * Overrides incorrect names from Viem where necessary.
 */
export const CHAIN_EXPLORER_NAMES = SUPPORTED_CHAINS.reduce<{
  [key: number]: string;
}>((explorerNames, chain) => {
  const defaultName = chain.blockExplorers?.default.name || 'Explorer';

  // Override incorrect block explorer names from Viem
  if (chain.id === CHAIN_IDS.LINEA) {
    explorerNames[chain.id] = 'Lineascan';
  } else {
    explorerNames[chain.id] = defaultName;
  }

  return explorerNames;
}, {});
